import {Chip} from '@dropbox/dig-components/chip';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {PinLine} from '@dropbox/dig-icons/assets';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {pulseUserAtom} from 'atoms/auth';
import {newsPostIdAtom} from 'atoms/news';
import {NewsPostContent} from 'client';
import {ButtonLink} from 'components/DSYS/Link';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {useSkeletonFadeIn} from 'components/DSYS/SkeletonFadeIn';
import {Title} from 'components/DSYS/Title';
import {NewsPostRightLoading} from 'components/news/NewsLoadingState';
import {format} from 'date-fns';
import {isContentEditor} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {useAtom, useAtomValue} from 'jotai';
import {NotFound} from 'pages/NotFound';
import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router';

import {useAddViewer, usePostSuspense} from './hooks';
import styles from './NewsPostRight.module.css';

export const NewsPostRight = (props: {id: number}) => {
  const [loadedNewsPostId, setLoadedNewsPostId] = useAtom(newsPostIdAtom);
  const toLoad = loadedNewsPostId ? loadedNewsPostId : props.id;
  const newspost = usePostSuspense(toLoad);
  const pulseUser = useAtomValue(pulseUserAtom);
  const isEditorView = pulseUser && isContentEditor(pulseUser);

  useEffect(() => {
    if (props.id !== loadedNewsPostId) {
      setLoadedNewsPostId(props.id);
    }
  }, [loadedNewsPostId, props.id, setLoadedNewsPostId]);

  useDocumentTitle(newspost.title);
  const fadeInRef = useSkeletonFadeIn(!pulseUser);

  if (!pulseUser) {
    return <NewsPostRightLoading />;
  }

  if (newspost.is_draft && !isEditorView) {
    return <NotFound />;
  }

  return (
    <div ref={fadeInRef} className={styles.newsPostRight}>
      {/* Editors get Edit on the title row's far right, like a team's. */}
      <Box display="flex" alignItems="center" className={styles.title} style={{gap: 8}}>
        <Title weightVariant="emphasized" size="large" className={atoms({flexShrink: 1})}>
          {newspost.title}
        </Title>
        {isEditorView && (
          <Box flexGrow={1} display="flex" justifyContent="flex-end">
            <ButtonLink
              to={`/news/${newspost.id}/edit`}
              variant="outline"
              onClick={() =>
                analyticsLogger().logEvent('NEWS_EDIT_POST_CLICKED', {newspost_id: newspost.id})
              }
            >
              {t('edit')}
            </ButtonLink>
          </Box>
        )}
      </Box>
      <Info newspost={newspost} />
      <RichTextArea
        key={loadedNewsPostId}
        value={newspost.body}
        profile="paper"
        source={`news-perview-${loadedNewsPostId}`}
      />
    </div>
  );
};

const Info = ({newspost}: {newspost: NewsPostContent}) => {
  const navigate = useNavigate();

  const date = new Date(newspost.created_at);
  const august29 = new Date('2024-08-29');
  const options = {year: 'numeric', month: 'long', day: 'numeric'};
  const formattedDate = date.toLocaleDateString('en-US', options as Intl.DateTimeFormatOptions);

  return (
    <div className={styles.info}>
      {newspost.pin_order !== null && (
        <UIIcon src={PinLine} size="small" className={atoms({color: 'Text Subtle'})} />
      )}
      {newspost.is_draft && !newspost.post_at && (
        <Chip size="small" className={styles.draftChip}>
          <Chip.Content>{t('draft')}</Chip.Content>
        </Chip>
      )}
      {newspost.audience === 'Managers' && (
        <Chip size="small" className={atoms({backgroundColor: 'Selection Surface - State 1'})}>
          <Chip.Content>{t('audience_Managers')}</Chip.Content>
        </Chip>
      )}
      {newspost.category.map((category) => (
        <Chip
          key={category.name}
          size="small"
          onClick={() =>
            navigate('/news/' + category.name.toLowerCase(), {
              state: {source: 'news-category-chip'},
            })
          }
        >
          <Chip.Content>{category.name}</Chip.Content>
        </Chip>
      ))}

      {newspost.is_draft && newspost.post_at ? (
        <Text color="faint">
          {t('draft_scheduled', {
            date: format(new Date(newspost.post_at), "MMMM d 'at' haaa"),
          })}
        </Text>
      ) : (
        <Text color="subtle">{formattedDate}</Text>
      )}

      {date >= august29 && !newspost.is_draft && <ViewCountLabel id={newspost.id} />}
    </div>
  );
};

const ViewCountLabel = ({id}: {id: number}) => {
  const {addViewer, isPending} = useAddViewer();
  const [viewCount, setViewCount] = useState(0);

  useEffect(() => {
    (async () => {
      setViewCount(await addViewer({id: id}));
    })();
  }, [addViewer, id, viewCount]);

  const viewLabel = `${viewCount} view${viewCount !== 1 ? 's' : ''}`;

  if (viewCount === 0) {
    return null;
  }

  return isPending ? (
    <Skeleton.Text width={48} />
  ) : (
    <>
      <Text color="subtle"> • </Text>
      <Text color="subtle">{viewLabel}</Text>
    </>
  );
};
